import { Icon } from "@/components/icon";
import { graphql, useFragment } from "react-relay";
import type { spacePaywall_space$key } from "./__generated__/spacePaywall_space.graphql";
import { Card } from "@/components/ui/card";
import { useMemo } from "react";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { EditorContent } from "@tiptap/react";
import { useEditor } from "@/editor/editor";
import { Link, useLoaderData } from "@tanstack/react-router";
import { Separator } from "@/components/ui/separator";

type SpacePaywallProps = {
	space: spacePaywall_space$key;
};

export function SpacePaywall({ space }: SpacePaywallProps) {
	const { club } = useLoaderData({ from: "__root__" });
	const data = useFragment<spacePaywall_space$key>(
		graphql`
    fragment spacePaywall_space on Space {
      id

      primaryProduct {
        id
        productName
        accessDefaultDuration
        description
        checkoutUrl
        basePrice

        accessGroup {
          accessGroup {
            courses {
              id
              course {
                name
              }
            }
            spaces {
              id
              name
            }
          }
        }
      }
    }
  `,
		space,
	);

	const durationLabel = useMemo(() => {
		if (!data?.primaryProduct?.accessDefaultDuration) {
			return "/ano";
		}

		if (
			data.primaryProduct?.accessDefaultDuration >= 28 &&
			data.primaryProduct?.accessDefaultDuration < 32
		) {
			return "/mês";
		}

		if (
			data.primaryProduct?.accessDefaultDuration >= 87 &&
			data.primaryProduct?.accessDefaultDuration < 96
		) {
			return "/trimestre";
		}

		if (
			data.primaryProduct?.accessDefaultDuration >= 360 &&
			data.primaryProduct?.accessDefaultDuration < 370
		) {
			return "/ano";
		}

		return "";
	}, [data.primaryProduct?.accessDefaultDuration]);

	const editor = useEditor({
		editable: false,
		content: data.primaryProduct?.description,
		club: club.slug,
		features: {
			colors: { enabled: true },
			button: { enabled: true },
			mention: { enabled: true },
			placeholder: { enabled: true },
			attachments: { enabled: true },
			video: { enabled: true },
			poll: { enabled: true },
			youtube: { enabled: true },
			image: { enabled: true },
			iframe: { enabled: true },
			styling: { enabled: true },
			files: { enabled: true },
		},
	});

	if (
		data.primaryProduct?.id &&
		(data.primaryProduct?.accessGroup.accessGroup.courses.length ||
			data.primaryProduct?.accessGroup.accessGroup.spaces.filter(Boolean)
				.length)
	) {
		return (
			<>
				<div />
				<div className="w-full flex flex-col gap-6 max-w-[600px]">
					<div className="font-semibold text-2xl/6 px-6 flex flex-col items-center justify-start">
						<div className="rounded-md bg-muted text-muted-foreground size-16 flex items-center justify-center">
							<Icon name="Lock" className="size-8" />
						</div>

						<span className="text-balance text-center mt-6">
							Desbloqueie o acesso exclusivo
						</span>

						<span className="text-sm text-muted-foreground mt-3">
							Junte-se aos membros que já estão transformando os resultados
						</span>
					</div>
					<div className="size-full flex items-start justify-center pb-6">
						<Card className="p-0 border border-primary w-full flex flex-col gap-0">
							<div className="p-6 pb-0 w-full flex items-center justify-between">
								<span className="font-semibold text-lg">
									{data.primaryProduct?.productName}
								</span>

								{data.primaryProduct.basePrice &&
								parseFloat(data.primaryProduct.basePrice) > 0.01 ? (
									<span className="font-semibold text-lg/6">
										{parseFloat(
											data.primaryProduct?.basePrice || "0",
										).toLocaleString("pt-BR", {
											style: "currency",
											currency: "BRL",
										})}{" "}
										<span className="text-xs">{durationLabel}</span>
									</span>
								) : null}
							</div>

							<div className="px-6">
								<span className="text-muted-foreground text-sm/6">
									O que está incluso:
								</span>

								{data.primaryProduct?.accessGroup.accessGroup.courses.length ? (
									<Collapsible className="border rounded-md mt-6">
										<CollapsibleTrigger className="flex flex-row gap-3 w-full p-3 justify-between items-center">
											<div className="flex flex-row gap-3 items-center justify-start">
												<Icon name="MessageCirclePlus" className="size-4" />

												<div className="flex flex-col gap-1 items-start">
													<span className="text-sm/5 font-semibold">
														{
															data.primaryProduct?.accessGroup.accessGroup
																.courses.length
														}{" "}
														cursos
													</span>

													<span className="text-xs/4 text-muted-foreground line-clamp-1">
														Aulas Gravadas
													</span>
												</div>
											</div>

											<div>
												<Icon name="ChevronDown" className="size-4" />
											</div>
										</CollapsibleTrigger>

										<CollapsibleContent>
											<div className="px-3">
												<Separator />
											</div>

											<ul className="flex flex-col gap-3 text-sm text-muted-foreground mb-3 px-3 mt-3">
												{data.primaryProduct?.accessGroup.accessGroup.courses.map(
													(s) => (
														<li
															key={s.id}
															className="flex items-center justify-start gap-3"
														>
															<Icon
																name="Check"
																className="size-4 text-primary"
															/>
															{s.course.name}
														</li>
													),
												)}
											</ul>
										</CollapsibleContent>
									</Collapsible>
								) : null}

								{data.primaryProduct?.accessGroup.accessGroup.spaces.filter(
									Boolean,
								).length ? (
									<Collapsible className="border rounded-md mt-3">
										<CollapsibleTrigger className="flex flex-row gap-3 w-full p-3 justify-between items-center">
											<div className="flex flex-row gap-3 items-center justify-start">
												<Icon name="GraduationCap" className="size-4" />

												<div className="flex flex-col gap-1 items-start">
													<span className="text-sm/5 font-semibold">
														{
															data.primaryProduct?.accessGroup.accessGroup.spaces.filter(
																Boolean,
															).length
														}{" "}
														feeds
													</span>

													<span className="text-xs/4 text-muted-foreground">
														Discussões na comunidade
													</span>
												</div>
											</div>

											<div>
												<Icon name="ChevronDown" className="size-4" />
											</div>
										</CollapsibleTrigger>

										<CollapsibleContent>
											<div className="px-3">
												<Separator />
											</div>

											<ul className="flex flex-col gap-3 text-sm text-muted-foreground px-3 mb-3 mt-3">
												{data.primaryProduct?.accessGroup.accessGroup.spaces
													.filter(Boolean)
													.map((s) => (
														<li
															key={s.id}
															className="flex items-center justify-start gap-3"
														>
															<Icon
																name="Check"
																className="size-4 text-primary"
															/>
															{s.name}
														</li>
													))}
											</ul>
										</CollapsibleContent>
									</Collapsible>
								) : null}
							</div>

							<div className="p-6 flex flex-col gap-6">
								{!editor?.isEmpty ? <EditorContent editor={editor} /> : null}

								{data.primaryProduct?.checkoutUrl ? (
									<Link target="_blank" to={data.primaryProduct.checkoutUrl}>
										<Button className="w-full">Comprar Agora</Button>
									</Link>
								) : null}
							</div>
						</Card>
					</div>
				</div>
				<div />
			</>
		);
	}

	return (
		<div className="size-full p-6 md:p-12 lg:p-32 flex items-center justify-center">
			<div className="font-semibold text-2xl/6 mt-6 px-6 flex flex-col items-center justify-start gap-6">
				<div className="rounded-md bg-destructive text-destructive-foreground size-16 flex items-center justify-center">
					<Icon name="Lock" className="size-8" />
				</div>

				<span className="text-balance text-center">
					Você não tem permissão para acessar esse feed.
				</span>
			</div>
		</div>
	);
}
